'use client';

import React from 'react';
import styled from 'styled-components';

export interface EmptyStateProps {
  /** Optional decorative icon — render small (24-32px) and muted. */
  icon?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  /** Optional primary action node. */
  action?: React.ReactNode;
  className?: string;
}

const Surface = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${(p) => p.theme.space[8]} ${(p) => p.theme.space[6]};
  gap: ${(p) => p.theme.space[2]};
`;

const IconSlot = styled.div`
  color: ${(p) => p.theme.colors.neutral.inkFaint};
  margin-bottom: ${(p) => p.theme.space[2]};
`;

const Title = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const Body = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  line-height: 1.55;
  max-width: 44ch;
`;

const ActionSlot = styled.div`
  margin-top: ${(p) => p.theme.space[3]};
`;

/**
 * "No content yet" placeholder for empty regions: empty alerts list,
 * empty schedule, no-results search. Keep titles short and bodies
 * directive (what the user can do to fill the region).
 */
export function EmptyState({ icon, title, body, action, className }: EmptyStateProps) {
  return (
    <Surface className={className}>
      {icon !== undefined && <IconSlot aria-hidden="true">{icon}</IconSlot>}
      <Title>{title}</Title>
      {body !== undefined && <Body>{body}</Body>}
      {action !== undefined && <ActionSlot>{action}</ActionSlot>}
    </Surface>
  );
}
